import { useEffect, useState, useCallback, useMemo } from "react";
import { LayoutComponent } from "../LayoutComponent";
import {
  Flex,
  Content,
  View,
  ProgressCircle,
  Link,
  Text,
  DialogContainer,
  Dialog,
  Heading,
} from "@adobe/react-spectrum";
import moment from "moment";
import { formatDecimalToString } from "../../../../utils/helperUtil";
import { useDependency } from "../../../../contexts/DependencyProvider";
import { ListProjectItemRequest } from "../../../../services/soap/item/requests/ListProjectItemRequest";
import { ListProjectItemRequestOptions_ProjectItem } from "../../../../services/soap/item/requests/ListProjectItemRequestOptions";
import { useTranslation } from "react-i18next";
import { GetItemRequest } from "../../../../services/soap/item/requests/GetItemRequest";
import useComonentReload from "../../../../hooks/UseComponentReload";
import { useViewInfo } from "../../../../hooks/UseViewInfo";
import { ToastQueue } from '@adobe/react-spectrum';
import { TreeGridComponent } from "../../../ui/TreeGridComponent/TreeGridComponent";
import {
  TreeGridRow,
  TreeGridColumn,
} from "../../../ui/TreeGridComponent/types";
import ProjectProductNewActivityAddEdit from "./ProjectProductNewActivityAddEdit";
import styles from "./project_product_new_activity_component.module.css";

interface ProjectProductTreeNewActivityProps {
  readonly attrib0ColumnName?: string;
  readonly attrib1ColumnName?: string;
  readonly attrib2ColumnName?: string;
  readonly attrib0ColumnVisible?: boolean;
  readonly attrib1ColumnVisible?: boolean;
  readonly attrib2ColumnVisible?: boolean;
  readonly billingDateColumnName?: string;
  readonly billingDateColumnVisible?: boolean;
  readonly componentTitle?: string;
  readonly minHeight?: number;
  readonly percentWidth?: number;
  readonly productType?: number;
  readonly loadProductType?: number;
  readonly quantityColumnName?: string;
  readonly quantityColumnVisible?: boolean;
  readonly serialNumberColumnName?: string;
  readonly serialNumberColumnVisible?: boolean;
  readonly serialNumberComponentIdent?: string;
  readonly unitPriceColumnName?: string;
  readonly unitPriceColumnVisible?: boolean;
  readonly supplierColumnVisible?: boolean;
  readonly selectedItem?: any;
  readonly isNew?: boolean;
  readonly setData?: (dataItems: any[]) => void;
  readonly isRequired?: boolean;
  readonly projectId?: number;
  readonly parentItem?: any;
  readonly initialData?: any[];
}

export interface RowData {
  id: string;
  quantity: number;
  partID: string;
  _refId?: string;
  supplier?: string;
  supplierName?: string;
  partNumber: string;
  productName: string;
  currency: string;
  unitPrice: number;
  amountWithVat: number;
  vatValue: number;
  totalPrice: string;
  totalPriceWithVat: string;
  attrib0: string;
  attrib1: string;
  attrib2: string;
  refId: string;
  totalQuantity: number;
  amount: number;
  billingDate: string;
  billingDateFormatted?: string;
  unitPriceCurr?: string;
  totalPriceCurr?: string;
  purchasePrice?: number;
  totalAmountWithVat?: number;
  // Tree metadata
  parentId?: string | null;
  childrenIds?: string[];
  depth?: number;
  isExpanded?: boolean;
  itemSerial: string;
}

function ProjectProductTreeNewActivity({
  attrib0ColumnName = "Col.0",
  attrib1ColumnName = "Col.1",
  attrib2ColumnName = "Col.2",
  attrib0ColumnVisible = false,
  attrib1ColumnVisible = false,
  attrib2ColumnVisible = false,
  billingDateColumnName = "Billing Date",
  billingDateColumnVisible = false,
  componentTitle = "Project products",
  minHeight = 64,
  percentWidth = 100,
  productType = 2,
  loadProductType = 2,
  quantityColumnName = "Quantity",
  quantityColumnVisible = false,
  serialNumberColumnName = "Serial Number",
  serialNumberColumnVisible = false,
  serialNumberComponentIdent = "",
  unitPriceColumnName = "Unit Price",
  unitPriceColumnVisible = false,
  supplierColumnVisible = false,
  selectedItem,
  isNew = true,
  setData = () => {},
  isRequired,
  projectId,
  parentItem,
  initialData,
}: ProjectProductTreeNewActivityProps) {
  const { t } = useTranslation();
  const { isMobile } = useViewInfo();
  const pageSize = 50;
  const [reloadComponent] = useComonentReload();
  const { itemService, store } = useDependency();
  const [rowsData, setRowsData] = useState<RowData[]>([]);
  const [showLoader, setShowLoader] = useState<boolean>(true);

  // Pre-populate grid when initialData is provided (e.g. items transferred from parent)
  useEffect(() => {
    if (isNew && initialData && initialData.length > 0) {
      console.log("initialData", initialData);
      const withTree = buildTreeMetadata(initialData as RowData[]);
      console.log("withTree", withTree);
      setRowsData(withTree);
      setData(withTree);
      setShowLoader(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Track explicitly excluded children (children that were manually unchecked)
  const [excludedChildren, setExcludedChildren] = useState<Set<string>>(
    new Set(),
  );
  // When true, TreeGrid should select all loaded items (set when loading from project)
  const [selectAllOnNextLoad, setSelectAllOnNextLoad] = useState(false);
  // Add/Edit form state (match ProjectProductNewActivityComponent flow)
  const [showAddEditRow, setShowAddEditRow] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const defaultRowData: RowData = {
    id: "0",
    quantity: 1,
    partID: "",
    _refId: "",
    partNumber: "",
    productName: "",
    currency: "",
    unitPrice: 0,
    amountWithVat: 0,
    vatValue: 0,
    totalPrice: "0",
    totalPriceWithVat: "0",
    attrib0: "",
    attrib1: "",
    attrib2: "",
    refId: "",
    totalQuantity: 0,
    amount: 0,
    supplier: "",
    supplierName: "",
    billingDate: moment().toISOString(),
    billingDateFormatted: moment().format("MM/DD/YYYY"),
    parentId: undefined,
    childrenIds: [],
    depth: 0,
    isExpanded: true,
    itemSerial: "",
  };

  const [selectedRow, setSelectedRow] = useState<RowData>(defaultRowData);

  // Build tree metadata from flat list
  const buildTreeMetadata = (items: RowData[]): RowData[] => {
    if (!items || items.length === 0) {
      return [];
    }

    const byId: Record<string, RowData> = {};
    const childrenMap: Record<string, string[]> = {};

    // Build a set of all IDs present in the dataset for quick lookup
    const allIds = new Set(items.map((item) => String(item.id)));

    items.forEach((item) => {
      const id = String(item.id);
      const rawParentId =
        item.parentId && item.parentId !== "" ? String(item.parentId) : null;
      // Only keep parentId if the parent actually exists in this dataset
      const parentId =
        rawParentId && allIds.has(rawParentId) ? rawParentId : null;

      byId[id] = {
        ...item,
        id,
        parentId,
        childrenIds: [],
        depth: 0,
        isExpanded: item.isExpanded ?? true,
      };

      if (parentId) {
        if (!childrenMap[parentId]) {
          childrenMap[parentId] = [];
        }
        childrenMap[parentId].push(id);
      }
    });

    Object.entries(childrenMap).forEach(([parentId, childIds]) => {
      if (byId[parentId]) {
        byId[parentId].childrenIds = childIds;
      }
    });

    const computeDepth = (item: RowData): number => {
      let depth = 0;
      let currentParentId = item.parentId;
      const visited = new Set<string>();

      while (
        currentParentId &&
        byId[currentParentId] &&
        !visited.has(currentParentId)
      ) {
        visited.add(currentParentId);
        depth += 1;
        currentParentId = byId[currentParentId].parentId ?? null;
      }

      return depth;
    };

    Object.values(byId).forEach((item) => {
      item.depth = computeDepth(item);
    });

    return items.map((item) => byId[String(item.id)]);
  };

  // Find all descendants of a given item ID
  const findAllDescendants = useCallback(
    (itemId: string, rows: RowData[]): string[] => {
      const byId = new Map(rows.map((r) => [r.id, r]));
      const descendants: string[] = [];

      const visit = (id: string) => {
        const row = byId.get(id);
        if (row?.childrenIds && row.childrenIds.length > 0) {
          row.childrenIds.forEach((childId) => {
            descendants.push(childId);
            visit(childId);
          });
        }
      };

      visit(itemId);
      return descendants;
    },
    [],
  );

  // Convert selection to flat array (removing tree metadata)
  const selectionToFlatList = useCallback(
    (selectedIdsArray: string[], treeRows: RowData[]): RowData[] => {
      const selectedRows = treeRows.filter((row) =>
        selectedIdsArray.includes(row.id),
      );
      return selectedRows.map((row) => {
        const { childrenIds, depth, isExpanded, ...flatRow } = row;
        return flatRow as RowData;
      });
    },
    [],
  );

  // Compute effective selection (selected IDs + auto-added descendants, minus excluded children)
  const getEffectiveSelectedIds = useCallback(
    (
      selIds: Set<string>,
      rows: RowData[],
      excluded: Set<string>,
    ): Set<string> => {
      const rowsById = new Map(rows.map((r) => [r.id, r]));
      const effective = new Set(selIds);
      selIds.forEach((id) => {
        const row = rowsById.get(id);
        if (row?.childrenIds?.length) {
          const descendants = findAllDescendants(id, rows);
          descendants.forEach((descId) => {
            if (!excluded.has(descId)) effective.add(descId);
          });
        }
      });
      return effective;
    },
    [findAllDescendants],
  );

  // Default data for a newly added row
  const getNewRowDefaultData = useCallback(
    (parentId: string | null): Partial<RowData> => {
      const currency = rowsData[0]?.currency ?? "";
      const billingDate = moment().toISOString();
      const billingDateFormatted = moment().format("MM/DD/YYYY");
      return {
        quantity: 1,
        partID: "",
        _refId: "",
        partNumber: "",
        productName: "",
        currency,
        unitPrice: 0,
        amountWithVat: 0,
        vatValue: 0,
        totalPrice: "0",
        totalPriceWithVat: "0",
        totalPriceCurr: "0 " + currency,
        unitPriceCurr: "0 " + currency,
        attrib0: "",
        attrib1: "",
        attrib2: "",
        refId: "",
        totalQuantity: 0,
        amount: 0,
        billingDate,
        billingDateFormatted,
        parentId: parentId ?? undefined,
        childrenIds: [],
        depth: 0,
        isExpanded: true,
      };
    },
    [rowsData],
  );

  // Open Add form (match ProjectProductNewActivityComponent: don't add directly to grid)
  const handleOpenAddForm = useCallback(() => {
    setSelectedRow(defaultRowData);
    setShowAddEditRow(true);
    if (isMobile) setIsOpen(true);
  }, [isMobile]);

  const handleRowClose = useCallback(() => {
    setSelectedRow(defaultRowData);
    setShowAddEditRow(false);
    if (isMobile) setIsOpen(false);
  }, [isMobile]);

  const handleEditRow = useCallback(
    (_rowId: string, row: TreeGridRow<RowData>) => {
      setSelectedRow({ ...defaultRowData, ...row.data });
      setShowAddEditRow(true);
      if (isMobile) setIsOpen(true);
    },
    [isMobile, defaultRowData],
  );

  const closeDialog = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Resolve parent for new row (single selected row that can have children)
  const resolveParentIdForNewRow = useCallback((): string | null => {
    if (selectedIds.size !== 1) return null;
    const selectedId = Array.from(selectedIds)[0];
    const row = rowsData.find((r) => r.id === selectedId);
    if (row && ((row.childrenIds?.length ?? 0) > 0 || row.parentId != null)) {
      return selectedId;
    }
    return null;
  }, [selectedIds, rowsData]);

  // Fetch item relations (COMPONENT) via GetItem and return child rows to add under main row
  const fetchRelatedComponents = useCallback(
    async (
      partID: string,
      parentRowId: string,
      billingDate: string,
    ): Promise<RowData[]> => {
      if (!partID) return [];
      try {
        const response = await itemService.getItem(
          new GetItemRequest(store.Server, store.SessionId, partID),
        );
        if (response?.result !== "OK" || !response.ITEM) return [];

        const item = Array.isArray(response.ITEM)
          ? response.ITEM[0]
          : response.ITEM;
        const comps = item.COMPONENT;
        if (!comps) return [];

        const compList = Array.isArray(comps) ? comps : [comps];
        const children = compList.filter((c: any) => c.isParent !== "1");
        if (children.length === 0) return [];

        const childRows: RowData[] = [];
        for (let i = 0; i < children.length; i++) {
          const c = children[i];
          const compPartID = c.component ?? "";
          const compQuantity = Number.parseFloat(c.countMin || "1") || 1;
          const compUnitPrice = Number.parseFloat(c.amount || "0") || 0;
          const compTotal = compQuantity * compUnitPrice;
          const currency = (c.currencyName || c.currency) ?? "";
          const partNumber = await getPartNumber({ partID: compPartID });
          const childId = `row_${Date.now()}_comp_${i}_${Math.random().toString(36).slice(2)}`;

          childRows.push({
            id: childId,
            quantity: compQuantity,
            partID: compPartID,
            _refId: "",
            partNumber: partNumber || "Part Number",
            productName: c.componentName ?? "",
            currency,
            unitPrice: compUnitPrice,
            amountWithVat: Number.parseFloat(c.amountWithVat || "0") || 0,
            vatValue: Number(c.vatValue || 0),
            totalPrice: formatDecimalToString(compTotal.toFixed(2)),
            totalPriceWithVat: formatDecimalToString(
              (
                compQuantity * Number.parseFloat(c.amountWithVat || "0")
              ).toFixed(2),
            ),
            unitPriceCurr:
              formatDecimalToString(compUnitPrice.toFixed(2)) + " " + currency,
            totalPriceCurr:
              formatDecimalToString(compTotal.toFixed(2)) + " " + currency,
            attrib0: "",
            attrib1: "",
            attrib2: "",
            refId: "",
            totalQuantity: 0,
            amount: compUnitPrice,
            billingDate,
            billingDateFormatted: moment(billingDate).format("MM/DD/YYYY"),
            parentId: parentRowId,
            childrenIds: [],
            depth: 0,
            isExpanded: true,
            itemSerial: c.itemSerial ?? "",
          } as RowData);
        }
        return childRows;
      } catch (err) {
        console.error("Error fetching item relations:", err);
        return [];
      }
    },
    [itemService, store.Server, store.SessionId],
  );

  // Add record from form (confirm -> add to tree and persist); load related components via GetItem
  const addRecord = useCallback(
    async (record: any) => {
      try {
        if (!isNew || !setData) return;
        setShowLoader(true);

        const parentId = resolveParentIdForNewRow();
        const newId = `row_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        const billingDate =
          typeof record.billingDate === "string" &&
          record.billingDate.includes("T")
            ? record.billingDate.split("T")[0]
            : moment(record.billingDate).format("YYYY-MM-DD");

        const newRow: RowData = {
          ...record,
          id: newId,
          unitPriceCurr:
            formatDecimalToString(Number(record.unitPrice)) +
            " " +
            (record.currency ?? ""),
          billingDateFormatted: moment(record.billingDate).format("MM/DD/YYYY"),
          parentId: parentId ?? undefined,
          childrenIds: [],
          depth: 0,
          isExpanded: true,
        };

        const relatedRows = await fetchRelatedComponents(
          record.partID,
          newId,
          billingDate,
        );
        const allNewRows = [newRow, ...relatedRows];
        const idsToSelect = new Set(selectedIds);
        allNewRows.forEach((r) => idsToSelect.add(r.id));

        const withNew = buildTreeMetadata([...rowsData, ...allNewRows]);
        const effective = getEffectiveSelectedIds(
          idsToSelect,
          withNew,
          excludedChildren,
        );
        const flatList = selectionToFlatList(Array.from(effective), withNew);

        setRowsData(withNew);
        setSelectedIds(idsToSelect);
        setData(flatList);
        handleRowClose();
      } catch (error: any) {
        ToastQueue.negative(error.message ?? "Error adding item", {
          timeout: 3000,
        });
      } finally {
        setShowLoader(false);
      }
    },
    [
      isNew,
      setData,
      rowsData,
      selectedIds,
      resolveParentIdForNewRow,
      fetchRelatedComponents,
      getEffectiveSelectedIds,
      selectionToFlatList,
      excludedChildren,
      handleRowClose,
    ],
  );

  const updateRecord = useCallback(
    async (record: any) => {
      try {
        if (!isNew || !setData) return;
        record.unitPriceCurr =
          formatDecimalToString(Number(record.unitPrice)) +
          " " +
          (record.currency ?? "");
        record.billingDateFormatted = moment(record.billingDate).format(
          "MM/DD/YYYY",
        );
        const updated = rowsData.map((row) =>
          row.id === record.id ? { ...row, ...record } : row,
        );
        const withMeta = buildTreeMetadata(updated);
        const effective = getEffectiveSelectedIds(
          selectedIds,
          withMeta,
          excludedChildren,
        );
        const flatList = selectionToFlatList(Array.from(effective), withMeta);
        setRowsData(withMeta);
        setData(flatList);
        handleRowClose();
      } catch (error) {
        // no-op
      } finally {
        setShowLoader(false);
      }
    },
    [
      isNew,
      setData,
      rowsData,
      selectedIds,
      excludedChildren,
      getEffectiveSelectedIds,
      selectionToFlatList,
      handleRowClose,
    ],
  );

  const deleteRecord = useCallback(
    async (record: any) => {
      try {
        if (!isNew || !setData) return;
        const filtered = rowsData.filter((row) => row.id !== record.id);
        const withMeta = buildTreeMetadata(filtered);
        const nextSel = new Set(selectedIds);
        nextSel.delete(record.id);
        const effective = getEffectiveSelectedIds(
          nextSel,
          withMeta,
          excludedChildren,
        );
        const flatList = selectionToFlatList(Array.from(effective), withMeta);
        setRowsData(withMeta);
        setSelectedIds(nextSel);
        setData(flatList);
        handleRowClose();
      } catch (error) {
        console.error("deleteRecord Error", error);
      } finally {
        setShowLoader(false);
      }
    },
    [
      isNew,
      setData,
      rowsData,
      selectedIds,
      excludedChildren,
      getEffectiveSelectedIds,
      selectionToFlatList,
      handleRowClose,
    ],
  );

  // Effective selection count for validation (same logic as persistence)
  const effectiveSelectedIds = getEffectiveSelectedIds(
    selectedIds,
    rowsData,
    excludedChildren,
  );
  const effectiveFlatList = selectionToFlatList(
    Array.from(effectiveSelectedIds),
    rowsData,
  );
  const effectiveSelectionCount = effectiveFlatList.length;
  const isInvalid = Boolean(
    isRequired && effectiveSelectionCount === 0 && initialData?.length === 0,
  );

  // Get part number for an item
  const getPartNumber = async (item: any): Promise<string> => {
    try {
      const itemResp = await itemService.getItem(
        new GetItemRequest(store.Server, store.SessionId, item.partID),
      );
      return itemResp.ITEM.refId ?? "";
    } catch (error) {
      console.error("Error fetching part number:", error);
      return "";
    }
  };

  // Update project product data with part numbers
  const updateProjectProductData = async (
    projectProductData: RowData[],
  ): Promise<RowData[]> => {
    if (!projectProductData) return [];
    return Promise.all(
      projectProductData.map(async (item) => {
        const partNumber = await getPartNumber(item);
        return {
          ...item,
          _refId: `${partNumber}_${item.productName}_ref`,
          partNumber,
        };
      }),
    );
  };

  // Load project product data
  const loadProjectProductData = async () => {
    if (!projectId || projectId <= 0) {
      setShowLoader(false);
      return;
    }

    try {
      // Use same API pattern as ProjectProductNewActivityComponent
      // For serviced items (productType=4), use folder='', project=projectId, activity='', project_type='4'
      const projecrProdRes: any = await itemService.listProjectItem(
        new ListProjectItemRequest(
          store.Server,
          store.SessionId,
          pageSize,
          0,
          new ListProjectItemRequestOptions_ProjectItem(
            "",
            `${projectId}`,
            "",
            String(loadProductType),
          ),
        ),
      );

      if (projecrProdRes?.result === "OK") {
        // Map the project product data to desired format
        let projectProductData: RowData[] =
          projecrProdRes?.ROWS?.map((item: any) => {
            const quantityNum =
              Number.parseFloat(item.ITEM.quantity || "0") || 0;
            const unitPriceNum =
              Number.parseFloat(item.ITEM.amount || "0") || 0;
            const totalAmountNum =
              Number.parseFloat(item.ITEM.totalAmount || "0") || 0;
            const currency = item.ITEM.currency || "";

            return {
              id: item.ITEM.id,
              quantity: quantityNum,
              partID: item.ITEM.item,
              _refId: "",
              partNumber: "Part Number",
              productName: item.ITEM.name,
              supplier: item.ITEM.supplier,
              supplierName: item.ITEM.supplierName,
              currency,
              vatValue: Number(item.ITEM.vatValue || 0),
              amountWithVat: Number(item.ITEM.amountWithVat || 0),
              unitPrice: unitPriceNum,
              unitPriceCurr:
                formatDecimalToString(unitPriceNum.toFixed(2)) + " " + currency,
              amount: Number(item.ITEM.amount || 0),
              totalPrice: formatDecimalToString(totalAmountNum.toFixed(2)),
              totalPriceWithVat: formatDecimalToString(
                Number.parseFloat(item.ITEM.totalAmountWithVat || "0").toFixed(
                  2,
                ),
              ),
              totalPriceCurr:
                formatDecimalToString(totalAmountNum.toFixed(2)) +
                " " +
                currency,
              purchasePrice: item.ITEM.purchasePrice,
              totalAmountWithVat: item.ITEM.totalAmountWithVat,
              attrib0: item.ITEM.attrib0 || "",
              attrib1: item.ITEM.attrib1 || "",
              attrib2: item.ITEM.attrib2 || "",
              refId: item.ITEM.refId || "",
              totalQuantity: Number(item.ITEM.totalQuantity || 0),
              billingDate: item.ITEM.billingDate,
              billingDateFormatted: moment(item.ITEM.billingDate).format(
                "MM/DD/YYYY",
              ),
              parentId: item.ITEM.parentId || null,
              childrenIds: [],
              depth: 0,
              itemSerial: item.ITEM.itemSerial || "",
              isExpanded: true,
            };
          }) || [];

        // Enrich with partNumber via GetItemRequest
        projectProductData = await updateProjectProductData(projectProductData);

        // Build tree metadata
        const withTreeMeta = buildTreeMetadata(projectProductData);
        setRowsData(withTreeMeta);
        // Reset excluded children when data reloads
        setExcludedChildren(new Set());
        // Signal TreeGrid to select all loaded items by default
        setSelectAllOnNextLoad(true);
      }
    } catch (error) {
      console.error("Error loading project product data:", error);
    } finally {
      setShowLoader(false);
    }
  };

  // Handle selection change with automatic child resolution
  const handleSelectionChange = useCallback(
    (selectedIdsArray: string[]) => {
      setSelectedIds((prevSelectedIds) => {
        const newSelectedIds = new Set(selectedIdsArray);
        const rowsById = new Map(rowsData.map((r) => [r.id, r]));
        setExcludedChildren((prevExcluded) => {
          const newExcludedChildren = new Set(prevExcluded);

          // Track which children were explicitly unchecked (were selected before, not selected now, and have a parent)
          prevSelectedIds.forEach((id) => {
            if (!newSelectedIds.has(id)) {
              const row = rowsById.get(id);
              if (row && row.parentId) {
                // This is a child that was unselected
                newExcludedChildren.add(id);
              }
            }
          });

          // If a parent is selected again, remove its children from excluded list (user can re-enable auto-selection)
          selectedIdsArray.forEach((id) => {
            if (!prevSelectedIds.has(id)) {
              // Newly selected item
              const row = rowsById.get(id);
              if (row && row.childrenIds && row.childrenIds.length > 0) {
                // Remove descendants from excluded list when parent is re-selected
                const descendants = findAllDescendants(id, rowsData);
                descendants.forEach((descId) =>
                  newExcludedChildren.delete(descId),
                );
              }
            }
          });

          // Build effective selection: TreeGrid selection + auto-added children (excluding explicitly excluded ones)
          const effectiveSelection = new Set(newSelectedIds);

          // For each selected parent, add descendants (unless explicitly excluded)
          selectedIdsArray.forEach((id) => {
            const row = rowsById.get(id);
            if (row && row.childrenIds && row.childrenIds.length > 0) {
              const descendants = findAllDescendants(id, rowsData);
              descendants.forEach((descId) => {
                if (!newExcludedChildren.has(descId)) {
                  effectiveSelection.add(descId);
                }
              });
            }
          });

          // For each unselected parent, remove all descendants from effective selection (cascading deletion)
          prevSelectedIds.forEach((id) => {
            if (!newSelectedIds.has(id)) {
              const row = rowsById.get(id);
              if (row && row.childrenIds && row.childrenIds.length > 0) {
                // Remove all descendants from effective selection
                const descendants = findAllDescendants(id, rowsData);
                descendants.forEach((descId) => {
                  effectiveSelection.delete(descId);
                  // Also remove from excluded list since they're removed via cascade
                  newExcludedChildren.delete(descId);
                });
              }
            }
          });

          // Convert to flat list and persist
          const flatList = selectionToFlatList(
            Array.from(effectiveSelection),
            rowsData,
          );
          setData(flatList);

          return newExcludedChildren;
        });

        return newSelectedIds;
      });
    },
    [rowsData, findAllDescendants, selectionToFlatList, setData],
  );

  // Load data on mount and when dependencies change
  useEffect(() => {
    // If initialData is already provided, skip API load entirely — the initialData
    // useEffect (above) has already populated rowsData.
    if (isNew && initialData && initialData.length > 0) {
      return;
    }

    setShowLoader(true);
    (async () => {
      if (
        (productType === 3 || productType === 4) &&
        projectId &&
        projectId > 0
      ) {
        await loadProjectProductData();
      } else {
        setShowLoader(false);
      }
    })();
  }, [selectedItem, reloadComponent, projectId, productType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist current selection when component unmounts or dependencies change
  useEffect(() => {
    return () => {
      if (selectedIds.size > 0) {
        const flatList = selectionToFlatList(Array.from(selectedIds), rowsData);
        setData(flatList);
      }
    };
  }, [selectedIds, rowsData, selectionToFlatList, setData]);

  // Define columns for TreeGrid
  const [columns] = useState<TreeGridColumn<RowData>[]>([
    {
      key: "productName",
      header: t("product", { ns: "layout_components" }),
      width: 250,
      isTreeColumn: true,
      showFolderIcon: true,
    },
    {
      key: "partNumber",
      header: t("ref_id", { ns: "layout_components" }),
      width: 120,
    },
    {
      key: "itemSerial",
      header: t("serial_number", { ns: "layout_components" }),
      width: 200,
      hidden: !serialNumberColumnVisible,
    },
    {
      key: "supplierName",
      header: t("supplier", { ns: "layout_components" }),
      width: 200,
      hidden: !supplierColumnVisible,
    },
    {
      key: "attrib0",
      header: attrib0ColumnName,
      width: 120,
      hidden: !attrib0ColumnVisible,
    },
    {
      key: "attrib1",
      header: attrib1ColumnName,
      width: 120,
      hidden: !attrib1ColumnVisible,
    },
    {
      key: "attrib2",
      header: attrib2ColumnName,
      width: 120,
      hidden: !attrib2ColumnVisible,
    },
    {
      key: "itemSerial",
      header: serialNumberColumnName,
      width: 120,
      hidden: !serialNumberColumnVisible,
    },
    {
      key: "quantity",
      header: quantityColumnName,
      width: 100,
      align: "right",
      hidden: !quantityColumnVisible,
    },
    {
      key: "unitPriceCurr",
      header: unitPriceColumnName,
      width: 150,
      align: "right",
      hidden: !unitPriceColumnVisible,
    },
    {
      key: "billingDateFormatted",
      header: billingDateColumnName,
      width: 120,
      hidden: !billingDateColumnVisible,
    },
  ]);

  // Default selection: all loaded items, only when we just loaded from project (selectAllOnNextLoad)
  const defaultSelectedIds = useMemo(() => {
    if (!selectAllOnNextLoad || rowsData.length === 0) return [];
    return rowsData.map((r) => String(r.id));
  }, [selectAllOnNextLoad, rowsData]);

  // Clear the flag after we've passed it to TreeGrid so it doesn't re-apply on add/update
  useEffect(() => {
    if (selectAllOnNextLoad && rowsData.length > 0) {
      setSelectAllOnNextLoad(false);
    }
  }, [selectAllOnNextLoad, rowsData]);

  // Map rowsData to TreeGridRow format
  const treeGridRows: TreeGridRow<RowData>[] = rowsData.map((item, index) => {
    const parentExists = item.parentId
      ? rowsData.some((r) => String(r.id) === String(item.parentId))
      : false;
    const resolvedParentId = parentExists ? String(item.parentId) : null;
    const resolvedDepth = parentExists ? (item.depth ?? 0) : 0;

    return {
      id: String(item.id || `row-${index}`),
      parentId: resolvedParentId,
      depth: resolvedDepth,
      childrenIds: item.childrenIds ?? [],
      isExpanded: item.isExpanded ?? true,
      rowType: "product",
      sortOrder: index,
      data: {
        ...item,
        parentId: resolvedParentId,
        depth: resolvedDepth,
      },
    };
  });

  if (showLoader) {
    return (
      <Flex width="100%" justifyContent={"center"} marginTop={10}>
        <ProgressCircle aria-label="Loading…" isIndeterminate />
      </Flex>
    );
  }

  return (
    <View position={"relative"} width={"100%"} minHeight={minHeight}>
      <Flex direction={"column"} gap={"size-150"} width={"100%"}>
        <Flex
          direction={"row"}
          alignItems={"start"}
          justifyContent={"space-between"}
          width={"100%"}
        >
          <Content
            position={"relative"}
            UNSAFE_className={styles.heading_text}
            UNSAFE_style={{
              borderRadius: "5px",
              padding: "10px",
              width: "auto",
            }}
          >
            {componentTitle}
          </Content>
          <Flex
            direction={"row"}
            alignItems={"center"}
            justifyContent={"center"}
            gap={"size-100"}
            UNSAFE_className={styles.icon_add_parent}
          >
            <Content>
              <Link
                isQuiet
                onPress={handleOpenAddForm}
                aria-label={t("add", { ns: "layout_components" })}
              >
                <i className="bi bi-plus fs-5">
                  <View UNSAFE_className={styles.icon_add_text}>
                    {t("add", { ns: "layout_components" })}
                  </View>
                </i>
              </Link>
            </Content>
          </Flex>
        </Flex>

        {isInvalid && (
          <div
            role="alert"
            aria-live="polite"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              width: "100%",
              minHeight: "32px",
              padding: "0 16px",
              backgroundColor: "#FFF0E5",
              border: "1px solid #DC3545",
              borderRadius: "5px",
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.333-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.647-1.743-2.98l5.58-9.92zM9 8a1 1 0 012 0v3a1 1 0 11-2 0V8zm1 5a1 1 0 100 2 1 1 0 000-2z"
                fill="#DC3545"
              />
            </svg>
            <Text
              UNSAFE_style={{
                color: "#DC3545",
                fontSize: "14px",
                fontWeight: 500,
                margin: 0,
              }}
            >
              {t("formView.requiredFieldsRibbon", {
                ns: "layout-components",
              }) || "At least one item is required."}
            </Text>
          </div>
        )}

        <View
          UNSAFE_style={{
            maxWidth: "100%",
            border: isInvalid ? "2px solid #DC3545" : undefined,
            borderRadius: isInvalid ? "5px" : undefined,
          }}
        >
          <Flex
            direction={"column"}
            width={"100%"}
            UNSAFE_style={{ maxWidth: "100%" }}
          >
            <TreeGridComponent<RowData>
              columns={columns}
              data={treeGridRows}
              enableKeyboardCreate={false}
              defaultSelectedIds={defaultSelectedIds}
              showSelection={true}
              showSelectAll={true}
              enableDragDrop={false}
              enableInlineEdit={false}
              enableKeyboardNav={true}
              showToolbar={false}
              showInfoIcon={true}
              onInfoClick={handleEditRow}
              showDeleteIcon={false}
              onSelectionChange={handleSelectionChange}
              maxHeight={400}
              emptyMessage={
                t("no_data_available", { ns: "layout_components" }) ||
                "No data available"
              }
              appendedRows={
                !isMobile && showAddEditRow ? (
                  <ProjectProductNewActivityAddEdit
                    id={String(selectedItem?.id)}
                    selectedItemType={selectedItem?.type}
                    defaultRowData={defaultRowData}
                    selectedRowData={selectedRow}
                    handleRowClose={handleRowClose}
                    addRecord={addRecord}
                    updateRecord={updateRecord}
                    deleteRecord={deleteRecord}
                    attrib0ColumnName={attrib0ColumnName}
                    attrib1ColumnName={attrib1ColumnName}
                    attrib2ColumnName={attrib2ColumnName}
                    attrib0ColumnVisible={attrib0ColumnVisible}
                    attrib1ColumnVisible={attrib1ColumnVisible}
                    attrib2ColumnVisible={attrib2ColumnVisible}
                    billingDateColumnName={billingDateColumnName}
                    billingDateColumnVisible={billingDateColumnVisible}
                    componentTitle={componentTitle}
                    quantityColumnName={quantityColumnName}
                    quantityColumnVisible={quantityColumnVisible}
                    serialNumberColumnName={serialNumberColumnName}
                    serialNumberColumnVisible={serialNumberColumnVisible}
                    unitPriceColumnName={unitPriceColumnName}
                    unitPriceColumnVisible={unitPriceColumnVisible}
                    supplierColumnVisible={supplierColumnVisible}
                    closeDialog={closeDialog}
                    styles={styles}
                    renderAsRow={true}
                  />
                ) : undefined
              }
            />
          </Flex>
        </View>

        {isMobile && (
          <DialogContainer isDismissable onDismiss={() => setIsOpen(false)}>
            {isOpen && (
              <Dialog>
                <Heading>
                  {selectedRow.id === "0"
                    ? t("add", { ns: "layout_components" })
                    : t("edit", { ns: "layout_components" })}
                </Heading>
                <Content>
                  <ProjectProductNewActivityAddEdit
                    id={String(selectedItem?.id)}
                    selectedItemType={selectedItem?.type}
                    defaultRowData={defaultRowData}
                    selectedRowData={selectedRow}
                    handleRowClose={handleRowClose}
                    addRecord={addRecord}
                    updateRecord={updateRecord}
                    deleteRecord={deleteRecord}
                    attrib0ColumnName={attrib0ColumnName}
                    attrib1ColumnName={attrib1ColumnName}
                    attrib2ColumnName={attrib2ColumnName}
                    attrib0ColumnVisible={attrib0ColumnVisible}
                    attrib1ColumnVisible={attrib1ColumnVisible}
                    attrib2ColumnVisible={attrib2ColumnVisible}
                    billingDateColumnName={billingDateColumnName}
                    billingDateColumnVisible={billingDateColumnVisible}
                    componentTitle={componentTitle}
                    quantityColumnName={quantityColumnName}
                    quantityColumnVisible={quantityColumnVisible}
                    serialNumberColumnName={serialNumberColumnName}
                    serialNumberColumnVisible={serialNumberColumnVisible}
                    unitPriceColumnName={unitPriceColumnName}
                    unitPriceColumnVisible={unitPriceColumnVisible}
                    supplierColumnVisible={supplierColumnVisible}
                    closeDialog={closeDialog}
                    styles={styles}
                  />
                </Content>
              </Dialog>
            )}
          </DialogContainer>
        )}
      </Flex>
    </View>
  );
}

export const ProjectProductTreeNewActivityComponent = LayoutComponent(
  ProjectProductTreeNewActivity,
);
